import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ChapterType, ICellOutput, QuotationCellsActions } from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { Yii } from "libraries/interfaces/Yii";
import { IFieldSpecs } from 'libraries/module/formbuilder/fields/interfaces/field-specs';
import { CostFormGeneratorService } from 'app_quotation/pages/cost/services/cost-form-generator/cost-form-generator.service';
import {
    IQuotationsSuppliersSearch
} from 'libraries/service/resources/quotation-quotations/quotation-quotations-resource.service';
import { ITableBlockCategoryConfig } from '../../cost-grid.component';
import { ICurrencyOption } from "libraries/service/resources/currency-list/currency-list-resource.service";
import { ITableRow } from 'app_quotation/shared/services/table-data/ITable';
import { BlockSupplierComponent } from './components/block-supplier/block-supplier.component';
import { EMoveMode } from "libraries/enums/e-move-mode";
import { ICoordsTable } from "libraries/service/table-keyboard-navigation/table-keyboard-navigation.service";
import { QuotationBlockCategoryCodeEnum } from "libraries/interfaces/quotation-block-category-code.enum";

const DEFAULT_ICON: ITableBlockCategoryConfig = {
    code: QuotationBlockCategoryCodeEnum.misc,
    icon: "fa fa-dollar-sign",
    wording: Yii.t('wui-quotation-js', 'divers')
};

@Component({
    selector: 'cost-grid-block-content',
    template: `
        <ng-template #template>
            <td-separator *ngIf="element.block.isFirstSectionBlock"
                          [blocksNumberInSection]="element.block.sectionLength"
                          [isLand]="isTripLandQuoteBlockElement"
                          [isTrip]="element.chapterType === chapterType.trip">
            </td-separator>
            <td-day [quotationOffset]="element.block.quotationOffset"
                    [beginDate]="beginDate"
                    *ngIf="!isAddRemoveAllowed"></td-day>
            <block-add-remove-button [disabled]="!isEditable || element.block.isEmpty"
                                     [showButton]="isAddRemoveAllowed"
                                     (notifyAddBlock)="addBlock()"
                                     (notifyRemoveBlock)="removeBlock()">
            </block-add-remove-button>

            <block-title [icon]="icon"
                         [isTripBlock]="isTripBlockElement"
                         [isTripLandQuoteBlock]="isTripLandQuoteBlockElement"
                         [isEmpty]="element.block.isEmpty"
                         [title]="element.block.title"
                         [fieldConfig]="fieldConfig"
                         [label]="label[element.chapterType]"
                         [isKeyboardOpen]="focusCoord | isSameCoord:rowIndex:-1"
                         (onFocusField)="emitFocusChange({rowIndex: rowIndex, colIndex: -1})">
            </block-title>

            <block-supplier #blockSupplier
                            [isKeyboardOpen]="focusCoord | isSameCoord:rowIndex:0"
                            [block]="element.block"
                            [isEditable]="isEditable && !isEmpty"
                            [suppliers]="suppliers"
                            [defaultCurrencies]="defaultCurrencies"
                            [quotationCurrency]="quotationCurrency"
                            (onCostChange)="onValueChange.emit($event)"
                            (onFocusField)="emitFocusChange({rowIndex: rowIndex, colIndex: 0})"
                            (onInputChange)="onMoveModeChange.emit(moveMode.grid);searchSuppliers($event);"
                            (onOpenPanel)="onMoveModeChange.emit(moveMode.componentCell)"
                            (onClosePanel)="onMoveModeChange.emit(moveMode.grid)">
            </block-supplier>
        </ng-template>

    `,
    styles: [`
        :host {
            display: none
        }

        block-title.trip-block-title td.mat-cell.block-data {
            border-right: 0 solid transparent;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CostGridBlockContentComponent implements OnInit, OnChanges {

    @Input()
    public element: ITableRow;
    @Input()
    public beginDate: string;
    @Input()
    public costBlockCategoryConfig: Map<string, ITableBlockCategoryConfig> = new Map();
    @Input()
    public isEditable: boolean = true;
    @Input()
    public suppliers: IQuotationsSuppliersSearch[] = [];
    @Input()
    public defaultCurrencies: ICurrencyOption[];
    @Input()
    public quotationCurrency: string;
    @Input()
    public isEmpty: boolean;
    @Input()
    public rowIndex: number;
    @Input()
    public focusCoord: ICoordsTable;

    @ViewChild("blockSupplier")
    public blockSupplier: BlockSupplierComponent;

    @Output()
    public readonly onValueChange: EventEmitter<ICellOutput> = new EventEmitter<ICellOutput>();
    @Output()
    public readonly onFocusField: EventEmitter<ICoordsTable> = new EventEmitter<ICoordsTable>();
    @Output()
    public readonly onAddBlockPlaceholder: EventEmitter<ICellOutput> = new EventEmitter<ICellOutput>();
    @Output()
    public readonly onMoveModeChange: EventEmitter<EMoveMode> = new EventEmitter<EMoveMode>();
    @Output()
    public readonly emptyAndFocus: EventEmitter<ICoordsTable> = new EventEmitter<ICoordsTable>();
    @Output()
    public readonly onSearchSuppliers: EventEmitter<{name: string, category: string}> = new EventEmitter<{name: string, category: string}>();

    @ViewChild('template', {read: TemplateRef, static: true})
    public template: TemplateRef<ElementRef>;

    public label: { [key in ChapterType]: string } = {
        [ChapterType.trip]: Yii.t('wui-quotation-js', 'Libellé du devis terrestre'),
        [ChapterType.services]: Yii.t('wui-quotation-js', 'Libellé du service'),
        [ChapterType.fees]: Yii.t('wui-quotation-js', 'Libellé des frais'),
    };
    public chapterType: typeof ChapterType = ChapterType;
    public formGroup: FormGroup;
    public fieldConfig: IFieldSpecs;
    public isAddRemoveAllowed: boolean = false;
    public isTripBlockElement: boolean;
    public isTripLandQuoteBlockElement: boolean;
    public colspan: number;
    public icon: string = DEFAULT_ICON.icon;
    public moveMode: typeof EMoveMode = EMoveMode;

    constructor(
        private readonly viewContainerRef: ViewContainerRef,
        private readonly costFormGeneratorService: CostFormGeneratorService,
    ) {
    }

    public ngOnInit(): void {
        this.viewContainerRef.createEmbeddedView(this.template);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.initfieldConfig();
        this.initBlockDefinition();

        if (changes.costBlockCategoryConfig || changes.element) {
            this.icon = this.getIcon(this.element && this.element.block && this.element.block.category);
        }

    }

    public emitFocusChange(coords: ICoordsTable): void {
        this.onFocusField.emit(coords);
    }

    public addBlock(): void {
        if (!this.element.block.isEmpty) {
            this.onAddBlockPlaceholder.emit({
                id: this.element.block.id
            });
        }
    }

    public removeBlock(): void {
        this.emitFocusChange({rowIndex: this.rowIndex, colIndex: -1});
        this.onValueChange.emit({
            itemType: QuotationCellsActions.removePriceBlock,
            id: this.element.block.id,
        });
    }

    public searchSuppliers(input): void {
        if (input && input !== '') {
            const search: { name: string, category: string } = {
                name: input,
                category: this.element.block.category
            };
            this.onSearchSuppliers.emit(search);
        }
    }

    private getIcon(categoryCode: string): string {
        if (this.costBlockCategoryConfig && categoryCode) {
            return (this.costBlockCategoryConfig.get(categoryCode.toLowerCase()) || DEFAULT_ICON).icon;
        }

        return DEFAULT_ICON.icon;
    }

    private initBlockDefinition(): void {
        this.isTripBlockElement = isTripBlock(this.element);
        this.isTripLandQuoteBlockElement = isTripLandQuoteBlock(this.element);
        this.isAddRemoveAllowed = this.isTripLandQuoteBlockElement || !this.isTripBlockElement;
    }

    private initfieldConfig(): void {
        this.formGroup = this.costFormGeneratorService.generateLabelGroup();
        this.formGroup.valueChanges
            .subscribe(({title}: { title: string }) => {
                const isEmpty: boolean = this.element.block.isEmpty;
                this.onValueChange.emit({
                    id: isEmpty ? null : this.element.block.id,
                    itemType: isEmpty ? QuotationCellsActions.createPriceBlock : QuotationCellsActions.updatePriceBlocTitle,
                    order: this.element.block.order || 1,
                    title,
                    chapterCode: this.element.chapterType
                });
            });
        this.fieldConfig = {
            formControlName: "title",
            formGroup: this.formGroup,
            className: "grid-style",
            label: this.label[this.element.chapterType],
            disabled: !this.isEditable,
        };
    }

}

function isTripBlock(element: ITableRow): boolean {
    return element.chapterType === ChapterType.trip;
}

function isTripLandQuoteBlock(element: ITableRow): boolean {
    return isTripBlock(element) && !element.block.quotationOffset && !element.block.isChapo;
}
